import { AxiosResponse, AxiosError } from 'axios';
import { toast } from 'react-toastify';
//
import apiClient from 'services/api';
import apiRoutes from 'services/api/routes';
import { getAccessToken } from 'lib/auth';
import history from 'lib/history';
import routes from 'router/routes';
import {
  CREATE_DEAL_SUCCESS,
  DELETE_DEAL,
  DELETE_DEAL_FAIL,
  DELETE_DEAL_SUCCESS,
  GET_DEALS,
  GET_DEALS_FAIL,
  GET_DEALS_SUCCESS,
  SAVE_DEAL,
  SAVE_DEAL_FAIL,
  SAVE_DEAL_SUCCESS,
  DELETE_DEALS_SUCCESS,
  IDealValidationErrors,
  SET_DEAL_SEARCH_FILTER,
  SET_DEAL_SEARCH_KEYWORD,
  ESearchFilter,
  SET_DEAL_SEARCH_STATUS_FILTER,
} from 'store/deals/types';
import { SET_VALIDATION_ERRORS } from 'store/deal-edit/types';
import { setSidebarVisibility, setSidebarHeaderVisibility } from 'store/sidebar/actions';
import { setLoading } from 'store/loading/actions';
import { ELoadingType } from 'store/loading/types';
import { toApi } from 'lib/helpers/deal-formatters';
import { getAxiosErrorMessage, handleError } from 'lib/helpers/error-handler';
import messages from 'constants/messages';
import { validateName, validateStatus } from 'lib/validators/deal';
import { setIsDealDirty, trimDealFields } from 'store/deal-edit/actions';
import { updateDealsAfterSave } from 'store/deal-comparison/actions';
import { evaluateParetoDeals } from 'store/pareto/actions';
import { evaluatePortfolioDeals } from 'store/portfolio/actions';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import { IStoreState } from 'store';

export const getDeals = () => (dispatch: ThunkDispatch<void, void, AnyAction>) =>
  new Promise<void>((resolve, reject) => {
    dispatch({
      type: GET_DEALS,
    });
    dispatch(setLoading(ELoadingType.OPENING_IMAGE));

    apiClient
      .get(apiRoutes.deals, {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      })
      .then((response: AxiosResponse) => {
        dispatch({
          type: GET_DEALS_SUCCESS,
          deals: response.data,
        });
        resolve();
      })
      .catch((error: AxiosError) => {
        const errorMessage = getAxiosErrorMessage(error);
        if (!error.message.includes('401')) {
          handleError(errorMessage);
          dispatch({
            type: GET_DEALS_FAIL,
            error: errorMessage,
          });
        }
        reject(errorMessage);
      })
      .finally(() => dispatch(setLoading(ELoadingType.NONE)));
  });

/**
 * Save button triggers multiple API calls:
 *    - POST /deal (or) PUT /deal/:deal_id
 *    - POST /evaluate/deal or GET /evaluate/deal/:deal_id
 */
export function saveDeal(callback?: Function) {
  return (dispatch: ThunkDispatch<IStoreState, void, AnyAction>, getState: () => IStoreState) => {
    // Trim input fields
    dispatch(trimDealFields());

    // Get state from dealEdit object
    const { dealEdit: deal } = getState();

    // Validation
    const errors: IDealValidationErrors = {
      name: validateName(deal.name),
      status: validateStatus(deal.status),
    };

    if (errors.name || errors.status) {
      // Build toast message
      return dispatch({ type: SET_VALIDATION_ERRORS, errors });
    }

    // Optional `id` param will dictate which endpoint to send POST request to
    const endpoint = deal.id ? `${apiRoutes.deals}/${deal.id}` : apiRoutes.deals;

    dispatch({ type: SAVE_DEAL });
    dispatch(setLoading(ELoadingType.SPINNER));

    return apiClient
      .post(endpoint, toApi(deal), {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      })
      .then((response: AxiosResponse) => {
        dispatch({
          type: deal.id ? SAVE_DEAL_SUCCESS : CREATE_DEAL_SUCCESS,
          deal: response.data,
        });

        // Update dirty status
        dispatch(setIsDealDirty(false));

        // Redirect (if necessary)
        if (!deal.id) {
          history.push(`${routes.deals}/${response.data.id}`);
        }

        // Show message
        toast.success(messages.success.saveDeal, { autoClose: 2000 });

        // Update deal in dealComparison
        dispatch(updateDealsAfterSave(deal));

        // Update deal in pareto
        dispatch(evaluateParetoDeals());

        // Update deal in portfolio
        dispatch(evaluatePortfolioDeals());

        // Trigger callback (if applicable)
        if (callback && typeof callback === 'function') {
          callback();
        }
      })
      .catch((error: AxiosError) => {
        const errorMessage = getAxiosErrorMessage(error);
        handleError(errorMessage);
        dispatch({
          type: SAVE_DEAL_FAIL,
          error: errorMessage,
        });
      })
      .finally(() => dispatch(setLoading(ELoadingType.NONE)));
  };
}

export function deleteDealPromise(id: string | undefined): Promise<AxiosResponse> {
  return apiClient.delete(`${apiRoutes.deals}/${id}`, {
    headers: {
      Authorization: `Bearer ${getAccessToken()}`,
    },
  });
}

export function deleteDeal(id: string) {
  return (dispatch: ThunkDispatch<void, void, AnyAction>) => {
    dispatch({ type: DELETE_DEAL });
    dispatch(setLoading(ELoadingType.SPINNER));

    deleteDealPromise(id)
      .then(() => {
        dispatch({ type: DELETE_DEAL_SUCCESS, id });
        dispatch(setSidebarVisibility(false));
        dispatch(setSidebarHeaderVisibility(true));
        history.push(routes.dashboard);
      })
      .catch((error: AxiosError) => {
        const errorMessage = getAxiosErrorMessage(error);
        handleError(errorMessage);
        dispatch({
          type: DELETE_DEAL_FAIL,
          error,
        });
      })
      .finally(() => dispatch(setLoading(ELoadingType.NONE)));
  };
}

export function deleteMultipleDeals(dealIds: (string | undefined)[]) {
  return (dispatch: ThunkDispatch<void, void, AnyAction>) => {
    const promises: Array<Promise<AxiosResponse>> = [];

    dealIds.forEach(id => {
      promises.push(deleteDealPromise(id));
    });

    dispatch(setLoading(ELoadingType.SPINNER));

    Promise.all(promises)
      .then(() => {
        dispatch({
          type: DELETE_DEALS_SUCCESS,
          ids: dealIds,
        });
        toast.success(messages.success.multipleDealsDelete, { autoClose: 5000 });
      })
      .catch(() => {
        handleError(messages.errors.multipleDealDelete);
      })
      .finally(() => dispatch(setLoading(ELoadingType.NONE)));
  };
}

export function setValidationErrors(errors: Error) {
  return {
    type: SET_VALIDATION_ERRORS,
    errors,
  };
}

export const setDealSearchFilter = (searchFilter: ESearchFilter) => ({
  type: SET_DEAL_SEARCH_FILTER,
  searchFilter,
});

export const setDealSearchStatusFilter = (statusFilter: string) => ({
  type: SET_DEAL_SEARCH_STATUS_FILTER,
  statusFilter,
});

export const setDealSearchKeyword = (searchKeyword: string) => ({
  type: SET_DEAL_SEARCH_KEYWORD,
  searchKeyword,
});
